import { Fragment, ReactNode } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

type Props = {
  open: boolean;
  onClose: () => void;
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
};

export default function SidePanel({ open, onClose, icon, title, subtitle, badge, footer, children }: Props) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] dark:bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 flex justify-end">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-[cubic-bezier(0.2,0.8,0.2,1)] duration-500"
            enterFrom="translate-x-full opacity-60"
            enterTo="translate-x-0 opacity-100"
            leave="transform transition ease-in duration-300"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="flex h-full w-full max-w-[560px] flex-col border-l border-edge bg-surface shadow-overlay">
              <header className="flex items-start gap-3 border-b border-edge p-5">
                {icon}
                <div className="min-w-0 flex-1">
                  <Dialog.Title className="break-words font-[Gilroy-Bold] text-[17px] leading-snug text-heading-color">{title}</Dialog.Title>
                  {subtitle ? <p className="mt-1 font-[Gilroy-Medium] text-[13px] text-light-gray">{subtitle}</p> : null}
                  {badge ? <div className="mt-2.5 flex flex-wrap gap-2">{badge}</div> : null}
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={onClose}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-edge text-light-gray transition-all hover:rotate-90 hover:bg-overlay hover:text-heading-color"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </header>
              <div className="custom-scrollbar stagger flex-1 space-y-4 overflow-y-auto p-5">{children}</div>
              {footer ? <footer className="flex flex-wrap gap-2 border-t border-edge p-4">{footer}</footer> : null}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
